import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckboxStyle,
  FilterTypeGrid,
  boldFont,
  centerDivContainer,
  checkboxLabel,
  filterAccordionSummary,
} from '../../styles';

//Filter Component
const Filter = props => {
  const { t } = useTranslation();
  const {
    productFamilyList = [],
    siteListData = [],
    statusListData = [],
    handleRefreshClick,
    filterMaxHeight = 467,
  } = props;

  const [filters, setFilters] = useState({
    productFamily: [],
    site: [],
    status: [],
  });

  const filterConfig = useMemo(
    () => ({
      productFamily: {
        data: productFamilyList,
        key: 'Name',
        label: t('Product_Family'),
        stateKey: 'productFamily',
      },
      site: {
        data: siteListData,
        key: 'SiteName',
        label: t('Sites_Impacted'),
        stateKey: 'site',
      },
      status: {
        data: statusListData,
        key: 'Status',
        label: t('Status'),
        stateKey: 'status',
      },
    }),
    [productFamilyList, siteListData, statusListData, t]
  );

  useEffect(() => {
    Object.entries(filterConfig).forEach(([_filterType, config]) => {
      if (config.data.length === 0) {
        return;
      }

      const newFilterList = config.data.filter(item => item.checked);
      setFilters(prev => ({
        ...prev,
        [config.stateKey]: newFilterList,
      }));
    });
  }, [filterConfig]);

  const handleFilterChange = useCallback(
    (filterType, event, data) => {
      const config = filterConfig[filterType];
      const value = event.target.value;

      if (value === 'all') {
        const currentFilters = filters[config.stateKey];
        const allSelected = config.data.length === currentFilters.length;

        setFilters(prev => ({
          ...prev,
          [config.stateKey]: allSelected
            ? []
            : config.data.map(item => ({ ...item, checked: true })),
        }));
        return;
      }

      setFilters(prev => {
        const list = [...prev[config.stateKey]];
        const index = list
          .map(item => item[config.key])
          .indexOf(data[config.key]);

        if (index === -1) {
          list.push(data);
        } else {
          list.splice(index, 1);
        }

        return {
          ...prev,
          [config.stateKey]: list,
        };
      });
    },
    [filters, filterConfig]
  );

  const isAllSelected = useCallback(
    filterType => {
      const config = filterConfig[filterType];
      return (
        config.data.length > 0 &&
        filters[config.stateKey].length > 0 &&
        config.data.length === filters[config.stateKey].length
      );
    },
    [filters, filterConfig]
  );

  const filteredUnits = {
    selectedSites: filters.site.length
      ? filters.site.map(item => item.SiteName).toString()
      : '',
    selectedStatus: filters.status.length
      ? filters.status.map(item => item.Status).toString()
      : '',
    selectedProductFamily: filters.productFamily.length
      ? filters.productFamily.map(item => item.Name).toString()
      : '',
  };

  const onRefreshClick = () => {
    handleRefreshClick(filteredUnits);
  };

  const renderFilterAccordion = filterType => {
    const config = filterConfig[filterType];
    const currentFilters = filters[config.stateKey];

    return (
      <Accordion key={filterType} style={FilterTypeGrid}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${filterType}-content`}
          id={`${filterType}-header`}
          style={filterAccordionSummary}
        >
          <Typography style={boldFont}>{config.label}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '0px 36px 0px 0px' }}>
          <Grid>
            <Grid style={centerDivContainer}>
              <Checkbox
                value="all"
                onChange={event => handleFilterChange(filterType, event, {})}
                checked={isAllSelected(filterType)}
                sx={CheckboxStyle}
              />
              <Typography>{t('SELECT_ALL')}</Typography>
            </Grid>
            {config.data.map((item, index) => {
              const checked = currentFilters.some(
                data => item[config.key] === data[config.key]
              );

              return (
                <Grid key={index} style={checkboxLabel}>
                  <Checkbox
                    value={item[config.key]}
                    onChange={event =>
                      handleFilterChange(filterType, event, item)
                    }
                    checked={checked}
                    sx={CheckboxStyle}
                  />
                  <Grid
                    sx={{
                      position: 'relative',
                      top: 10,
                      display: 'flex',
                      flexDirection: 'row',
                    }}
                  >
                    <Typography>{item[config.key]}</Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      <Button
        variant="contained"
        sx={{
          textTransform: 'none',
          width: '100%',
          height: '50px',
          marginBottom: '10px',
          fontSize: '16px',
          backgroundColor: '#1F305E',
          '&:hover': {
            backgroundColor: '#2a4073',
            color: '#ffffff',
          },
        }}
        onClick={onRefreshClick}
      >
        {t('Refresh_list_table')}
      </Button>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Grid
          sx={{
            maxHeight: filterMaxHeight,
            overflowY: 'scroll',
            padding: '10px',
          }}
        >
          {Object.keys(filterConfig).map(filterType =>
            renderFilterAccordion(filterType)
          )}
        </Grid>
      </Paper>
    </>
  );
};

export default Filter;
